import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Crown, ShieldCheck, UserCog, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

const LOGO_URL = "https://customer-assets-0z36b82j.emergentagent.net/job_ticketing-transport/artifacts/5p6cedk3_1000607872.png";

const ROLES = [
  { key: "createur", label: "Créateur", desc: "Propriétaire de l'agence", icon: Crown, color: "text-orange-500", bg: "bg-orange-50 dark:bg-orange-500/10" },
  { key: "admin", label: "Admin", desc: "Gestion opérationnelle", icon: ShieldCheck, color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-500/10" },
  { key: "staff", label: "Staff", desc: "Vente et validation", icon: UserCog, color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-500/10" },
];

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState("createur");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Connexion réussie");
      // Super admin → console dédiée; les autres rôles → espace agence
      const defaultTarget = data?.user?.role === "super_admin" ? "/superadmin" : "/app";
      const requestedFrom = location.state?.from?.pathname;
      // On respecte "from" seulement si le rôle a le droit d'y accéder
      let target = defaultTarget;
      if (requestedFrom) {
        const isSuper = data?.user?.role === "super_admin";
        const wantsAgency = requestedFrom.startsWith("/app");
        const wantsSuper = requestedFrom.startsWith("/superadmin");
        if ((isSuper && wantsSuper) || (!isSuper && wantsAgency)) target = requestedFrom;
      }
      nav(target, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-slate-50 dark:bg-slate-950">
      <div className="relative hidden md:block">
        <img src="https://images.pexels.com/photos/39080480/pexels-photo-39080480.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/75 via-slate-950/60 to-orange-500/40" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <img src={LOGO_URL} alt="Transport.pro" className="w-12 h-12 rounded-xl object-cover" />
            <span className="font-display font-bold text-xl">Transport.pro</span>
          </Link>
          <div>
            <h1 className="font-display text-4xl font-bold leading-tight max-w-md">Bienvenue de retour, prêt à vendre des billets ?</h1>
            <p className="mt-4 text-white/85 max-w-md">Accédez à votre espace agence et gérez vos réservations en quelques clics.</p>
          </div>
          <div className="text-xs text-white/60">Multi-tenant · Sécurisé · FCFA</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-6">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Transport.pro" className="w-10 h-10 rounded-xl object-cover" />
              <span className="font-display font-bold text-lg">Transport.pro</span>
            </Link>
          </div>
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="font-display text-2xl">Connexion</CardTitle>
              <p className="text-sm text-slate-500">Entrez vos identifiants pour continuer</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Role selector icons */}
              <div>
                <Label className="mb-2 block text-xs uppercase tracking-widest text-slate-500 font-semibold">Je me connecte en tant que</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="role-selector">
                  {ROLES.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setSelectedRole(r.key)}
                      data-testid={`role-btn-${r.key}`}
                      className={`p-3 rounded-xl border transition-all text-left ${
                        selectedRole === r.key
                          ? "border-orange-500 bg-orange-500/5 shadow-sm scale-[1.02]"
                          : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg grid place-items-center mb-2 ${r.bg}`}>
                        <r.icon className={`w-5 h-5 ${r.color}`} />
                      </div>
                      <div className="text-sm font-semibold">{r.label}</div>
                      <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{r.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input" placeholder="vous@exemple.com" autoComplete="email" />
                </div>
                <div>
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input" placeholder="••••••••" autoComplete="current-password" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                  className="w-full btn-pill bg-orange-500 hover:bg-orange-600 h-11">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connexion…</> : "Se connecter"}
                </Button>
                <div className="text-sm text-center text-slate-500">
                  Pas encore de compte ? <Link to="/register" className="text-orange-500 hover:underline font-medium" data-testid="link-to-register">Créer mon agence</Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
